import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
} from 'react'
import cx from 'classnames'
import styles from './SegmentedControl.module.css'

export type SegmentedControlValue = string | number

export interface SegmentedControlProps {
  /** The currently selected value. Nothing is selected when empty */
  value: SegmentedControlValue | null | undefined
  /** Called with the option's value when an option is selected */
  onChange: (value: SegmentedControlValue) => void
  /** Name shared by the radio inputs. Generated when not given */
  name?: string
  /** Accessible label for the group */
  'aria-label'?: string
  /** Id of the element that labels the group */
  'aria-labelledby'?: string
  /** Id of the element that describes the group (e.g. helper text) */
  'aria-describedby'?: string
  /** Disables every option */
  disabled?: boolean
  /** Additional CSS class */
  className?: string
}

interface SegmentedControlContextValue {
  name: string
  value: SegmentedControlValue | null | undefined
  disabled: boolean
  onChange: (value: SegmentedControlValue) => void
}

const SegmentedControlContext =
  createContext<SegmentedControlContextValue | null>(null)

/**
 * A row of options where exactly one can be picked, shown as
 * joined buttons with a highlight that slides to the selected one.
 *
 * Each option is a native radio input, so keyboard (arrow keys)
 * and screen readers work as they do for any radio group.
 */
export function SegmentedControl({
  value,
  onChange,
  name,
  disabled = false,
  className,
  children,
  ...aria
}: PropsWithChildren<SegmentedControlProps>) {
  const generatedName = useId()
  const controlRef = useRef<HTMLDivElement>(null)

  useHighlightPosition(controlRef, value)

  return (
    <SegmentedControlContext.Provider
      value={{
        name: name ?? generatedName,
        value,
        disabled,
        onChange,
      }}
    >
      <div
        ref={controlRef}
        role="radiogroup"
        aria-disabled={disabled || undefined}
        className={cx(styles.SegmentedControl, className, {
          [styles.isDisabled]: disabled,
        })}
        {...aria}
      >
        {children}
      </div>
    </SegmentedControlContext.Provider>
  )
}

export interface SegmentedControlOptionProps {
  /** The value reported to onChange when this option is picked */
  value: SegmentedControlValue
  /** Text shown on the option */
  label: ReactNode
  /** Disables just this option */
  disabled?: boolean
}

export function SegmentedControlOption({
  value,
  label,
  disabled = false,
}: SegmentedControlOptionProps) {
  const group = useContext(SegmentedControlContext)
  if (!group) {
    throw new Error(
      'SegmentedControlOption must be used inside a SegmentedControl'
    )
  }
  // Compare as strings: form values for a number option may
  // arrive as either a number or a string.
  const isActive =
    group.value != null && String(group.value) === String(value)
  const isDisabled = group.disabled || disabled

  return (
    <label
      className={cx(styles.SegmentedControl__segment, {
        [styles.isActive]: isActive,
        [styles.isDisabled]: isDisabled,
      })}
      data-active={isActive || undefined}
    >
      <input
        className={styles.SegmentedControl__input}
        type="radio"
        name={group.name}
        value={String(value)}
        checked={isActive}
        disabled={isDisabled}
        onChange={() => group.onChange(value)}
      />
      {label}
    </label>
  )
}

/**
 * Moves the highlight behind the selected segment.
 *
 * Segments are as wide as their labels, so the position is
 * measured from the DOM. It is measured again whenever the
 * control or a segment changes size (window resizes, web fonts
 * finishing loading) or options are added or removed, so the
 * highlight stays lined up.
 */
function useHighlightPosition(
  controlRef: React.RefObject<HTMLDivElement | null>,
  value: SegmentedControlValue | null | undefined
) {
  useLayoutEffect(() => {
    const control = controlRef.current
    if (!control) return

    const measure = () => {
      const active =
        control.querySelector<HTMLElement>('[data-active]')
      if (!active) {
        delete control.dataset.highlighted
        return
      }
      control.style.setProperty(
        '--highlight-width',
        `${active.offsetWidth}px`
      )
      control.style.setProperty(
        '--highlight-x-pos',
        `${active.offsetLeft}px`
      )
      control.dataset.highlighted = 'true'
    }
    measure()

    // Only animate after the first position is set, so the
    // highlight doesn't slide in from the left when it appears.
    const frame = requestAnimationFrame(() => {
      control.dataset.animated = 'true'
    })

    if (typeof ResizeObserver === 'undefined') {
      return () => cancelAnimationFrame(frame)
    }
    const resizeObserver = new ResizeObserver(measure)
    const observeSegments = () => {
      resizeObserver.observe(control)
      control
        .querySelectorAll('label')
        .forEach((segment) => resizeObserver.observe(segment))
    }
    observeSegments()

    // Options added or removed later also change the segment
    // widths, so watch those too and measure again.
    const mutationObserver = new MutationObserver(() => {
      observeSegments()
      measure()
    })
    mutationObserver.observe(control, { childList: true })

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [controlRef, value])
}
